import React, { useState } from 'react';
import { Layout } from '../../../../components/Layout.tsx';
import { serviceAPI } from '../../../../services/api.ts';
import { useAuth } from '../../../../contexts/AuthContext.tsx';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

const CrossMatchMaintenance = () => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedDescription, setSelectedDescription] = useState<string | null>(
    null
  );
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [counts, setCounts] = useState<{
    odooOnlyCount: number;
    localOnlyCount: number;
    totalCombined: number;
  } | null>(null);
  const { project, ensureSession } = useAuth();

  const handleProcess = async () => {
    try {
      setLoading(true);
      const session = await ensureSession();
      if (!session) {
        toast.error('Please Contact Your System Administrator');
        return;
      }
      const response = await serviceAPI.crossMatchMaintenance(
        fromDate,
        toDate,
        project?.odoo_configuration_id || '',
        session || undefined
      );
      if (response.success && response.data) {
        setRecords(response.data);
        setCounts({
          odooOnlyCount: response.odooOnlyCount || 0,
          localOnlyCount: response.localOnlyCount || 0,
          totalCombined: response.totalCombined || 0,
        });
      }
    } catch (error) {
      console.error('Error processing cross-match:', error);
      toast.error('Error processing cross-match');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToLocal = async (record: any) => {
    try {
      setSyncingId(record.id);
      const res = await serviceAPI.syncServiceToLocal(record);
      if (res.success) {
        handleProcess();
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncToOdoo = async (record: any) => {
    try {
      setSyncingId(record.id || record._id);
      const session = await ensureSession();
      const res = await serviceAPI.syncServiceToOdoo(
        record._id,
        project?.odoo_configuration_id || '',
        session || undefined
      );
      if (res.success) {
        handleProcess();
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setSyncingId(null);
    }
  };

  const filteredRecords = React.useMemo(() => {
    return records.filter((record) => {
      const isLocal = record.matchStatus === 'LOCAL_ONLY';
      const employeeName =
        (isLocal ? record.employee?.name : record.resolved_employee_name) || '';
      const vehicleName =
        (isLocal ? record.vehicle?.name : record.resolved_vehicle_name) || '';
      const employeeId = String(record.employee_id || '');
      const vehicleId = String(record.vehicle_id || '');
      const recordId = String(record.id || record._id || '');

      const query = searchQuery.toLowerCase();
      const matchesSearch =
        recordId.includes(query) ||
        employeeName.toLowerCase().includes(query) ||
        vehicleName.toLowerCase().includes(query) ||
        employeeId.includes(query) ||
        vehicleId.includes(query);

      const matchesStatus =
        statusFilter === 'ALL' || record.matchStatus === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [records, searchQuery, statusFilter]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <Layout>
      <div className="sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Cross-Match Maintenance
          </h1>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div>
              <label
                htmlFor="fromDate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                From Date
              </label>
              <input
                type="date"
                id="fromDate"
                value={fromDate}
                max={today}
                onChange={(e) => setFromDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white p-2.5"
              />
            </div>

            <div>
              <label
                htmlFor="toDate"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                To Date
              </label>
              <input
                type="date"
                id="toDate"
                value={toDate}
                max={today}
                onChange={(e) => setToDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white p-2.5"
              />
            </div>

            <div>
              <button
                onClick={handleProcess}
                disabled={!fromDate || !toDate || loading}
                className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : 'Process Records'}
              </button>
            </div>
          </div>

          {records.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="search"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by ID, employee or vehicle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white p-2.5"
                />
              </div>

              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  Status Filter
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:text-white p-2.5"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="ODOO_ONLY">Odoo Only</option>
                  <option value="LOCAL_ONLY">Local Only</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8">
          {counts && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
                  Missing in Local (Odoo Only)
                </div>
                <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-300">
                  {counts.odooOnlyCount}
                </div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <div className="text-sm font-medium text-indigo-800 dark:text-indigo-400">
                  Missing in Odoo (Local Only)
                </div>
                <div className="text-2xl font-bold text-indigo-900 dark:text-indigo-300">
                  {counts.localOnlyCount}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                  Total Mismatches
                </div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {counts.totalCombined}
                </div>
              </div>
            </div>
          )}
          {records.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Service Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredRecords.map((record, index) => {
                      const isLocal = record.matchStatus === 'LOCAL_ONLY';
                      const isOdoo = record.matchStatus === 'ODOO_ONLY';

                      return (
                        <tr key={record.id || index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {record.id || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {record.date
                              ? dayjs(record.date).format('DD/MM/YYYY')
                              : '-'}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white max-w-[150px] truncate cursor-pointer"
                            onClick={() => setSelectedDescription(record.name)}
                            title="Click to see full name"
                          >
                            {record.name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="flex flex-col">
                              <span>
                                {isLocal
                                  ? record.employee?.name
                                  : record.resolved_employee_name ||
                                    'Odoo record'}
                              </span>
                              {!isLocal && record.employee_id && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {record.employee_id}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="flex flex-col">
                              <span>
                                {isLocal
                                  ? record.vehicle?.name
                                  : record.resolved_vehicle_name ||
                                    'Odoo record'}
                              </span>
                              {!isLocal && record.vehicle_id && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {record.vehicle_id}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {isLocal ? (
                              <span>{record.cost} PKR</span>
                            ) : (
                              <span>{record.amount || '-'} PKR</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${
                              isOdoo
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400'
                            }`}
                            >
                              {record.matchStatus.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-3">
                              {record.matchStatus === 'LOCAL_ONLY' && (
                                <button
                                  onClick={() => handleSyncToOdoo(record)}
                                  disabled={!!syncingId}
                                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white transition-all ${
                                    syncingId === (record.id || record._id)
                                      ? 'bg-indigo-400 cursor-not-allowed'
                                      : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                  }`}
                                >
                                  {syncingId === (record.id || record._id) ? (
                                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                  ) : (
                                    <CloudArrowUpIcon className="-ml-1 mr-2 h-4 w-4" />
                                  )}
                                  {syncingId === (record.id || record._id)
                                    ? 'Syncing...'
                                    : 'Sync to Odoo'}
                                </button>
                              )}
                              {record.matchStatus === 'ODOO_ONLY' && (
                                <button
                                  onClick={() => handleSyncToLocal(record)}
                                  disabled={!!syncingId}
                                  className={`inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white transition-all ${
                                    syncingId === record.id
                                      ? 'bg-green-400 cursor-not-allowed'
                                      : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                  }`}
                                >
                                  {syncingId === record.id ? (
                                    <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                                  ) : (
                                    <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
                                  )}
                                  {syncingId === record.id
                                    ? 'Creating...'
                                    : 'Create Locally'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
              <p className="text-gray-500 dark:text-gray-400">
                {loading
                  ? 'Fetching records...'
                  : 'Select date range and click "Process Records" to see comparisons.'}
              </p>
            </div>
          )}
        </div>

        {/* Modal for Full Name / Tooltip */}
        {selectedDescription && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity"
                aria-hidden="true"
                onClick={() => setSelectedDescription(null)}
              >
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span
                className="hidden sm:inline-block sm:align-middle sm:h-screen"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Service Name
                      </h3>
                      <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 whitespace-pre-wrap break-words">
                        {selectedDescription}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setSelectedDescription(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CrossMatchMaintenance;
